/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import React, {useState} from "react";
import {useFormikContext} from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {DynamicFormFieldDefinition} from "qqq/components/forms/DynamicFormUtils";
import {useRowEditContext} from "../RowEditContext";

interface Props
{
   fieldName: string;
   formFieldDef: DynamicFormFieldDefinition;
}

/***************************************************************************
 * Compact file-picker for an editing row.  Unlike FileInputField (used on
 * RecordScreen), we render inline within the cell's fixed height — small
 * button plus a truncated filename that never overflows into sibling cells.
 ***************************************************************************/
export const BlobEditCell: React.FC<Props> = ({fieldName, formFieldDef}) =>
{
   const {setFieldValue} = useFormikContext<any>();
   const [fileName, setFileName] = useState<string | null>(null);
   const triggerAdjuster = useRowEditContext()?.handleFieldBlur;

   const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) =>
   {
      const file = event.currentTarget.files?.[0] ?? null;
      setFileName(file?.name ?? null);
      setFieldValue(fieldName, file);
      //////////////////////////////////////////////////////////////////////////////
      // Mirror PossibleValueEditCell / BooleanEditCell — fire the on-change form //
      // adjuster on commit so blob fields with onChangeFormAdjuster react in row //
      // edit the same way they do on RecordScreen.                               //
      //////////////////////////////////////////////////////////////////////////////
      triggerAdjuster?.(fieldName, file);
   };

   return (
      <Box sx={{display: "flex", alignItems: "center", gap: "6px", width: "100%", minWidth: 0}}>
         <Button
            variant="outlined"
            component="label"
            size="small"
            sx={{
               flex:          "0 0 auto",
               height:        "22px",
               minHeight:     "22px",
               px:            "8px",
               py:            0,
               fontSize:      "0.75rem",
               textTransform: "none",
               whiteSpace:    "nowrap",
            }}
         >
            Choose file
            <input
               id={fieldName}
               name={fieldName}
               type="file"
               hidden
               onChange={onFileChange}
            />
         </Button>
         <Box sx={{
            flex:         "1 1 auto",
            minWidth:     0,
            fontSize:     "0.75rem",
            color:        fileName ? "text.primary" : "text.secondary",
            whiteSpace:   "nowrap",
            overflow:     "hidden",
            textOverflow: "ellipsis",
         }}>
            {fileName ?? "No file chosen"}
         </Box>
      </Box>
   );
};
